import React from "react";
import { HStack, VStack } from "@UI/Components/Stack/Stack";
import { Text } from "@UI/Components/Text/Text";
import { Divider } from "@UI/Components/Divider/Divider";
import { Icon } from "@UI/Components/Icon/Icon";
import { LocationDot } from "@UI/Components/Icons/LocationDot";
import { Box } from "@UI/Components/Box/Box";
import { useServiceRequestFlow } from "@modules/ServiceRequest/hooks/useServiceRequestFlow";
import { Suitcase } from "@UI/Components/Icons/Suitcase";
import { Grid, GridItem } from "@UI/Components/Grid/Grid";
import { Image, AspectRatio } from "@UI/Components/Image/Image";
import { Tooltip } from "@UI/Components/Tooltip/Tooltip";
import { dateFromNow } from "@utils/dateFromNow";
import useI18n from "@I18n/hooks/useI18n";
import messages from "../messages";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";

export const ExpandedServiceRequestDetails = () => {
  const { serviceRequest } = useServiceRequestFlow();
  const pictures = serviceRequest?.pictures || [];
  const { formatMessage } = useI18n();

  const l3Cat = serviceRequest?.level3Categories;

  const level3Categories = Array.isArray(l3Cat) ? l3Cat.join(" - ") : undefined;

  return (
    <VStack w="full" maxWidth="100%" spacing="16px" alignItems="start">
      <HStack spacing="8px">
        {/*  making sure if the local file exists */}
        {serviceRequest?.pCategoryId > 1 &&
          serviceRequest?.pCategoryId < 12 && (
            <Image
              src={require(`assets/images/cat/svg/s_cat/${serviceRequest?.pCategoryId}.svg`)}
              w="20px"
              h="20px"
              alt={"Cat"}
            />
          )}
        <VStack w="full" spacing="0" alignItems="start">
          {messages[`s_cat.${serviceRequest?.sCategory}`] && (
            <Text color="gray.900" fontSize="1rem" fontWeight="700" m="0">
              <FormattedMessage
                {...messages[`s_cat.${serviceRequest?.sCategory}`]}
              />
            </Text>
          )}
          {level3Categories && (
            <Tooltip label={level3Categories}>
              <Text
                color="gray.700"
                fontSize={{ base: "0.875rem" }}
                m="0"
                noOfLines={2}
              >
                {level3Categories}
              </Text>
            </Tooltip>
          )}
        </VStack>
      </HStack>
      <Text fontSize={{ base: "0.875rem" }} m="0">
        {`${formatMessage(messages["serviceRequest.postedBy"], {
          clientName: serviceRequest?.clientName,
        })} ${dateFromNow(serviceRequest?.postedDate)}`}
      </Text>
      <Divider color="gray.300" />

      {[
        {
          icon: LocationDot,
          filedName: formatMessage(messages["serviceRequest.serviceAddress"]),
          filedValue: serviceRequest?.placeName,
        },
        {
          icon: Suitcase,
          filedName: formatMessage(
            messages["serviceRequest.serviceProviderType"]
          ),
          filedValue: formatMessage(
            messages[
              `serviceRequest.serviceProviderType.option${
                serviceRequest?.pro_only ? "2" : "1"
              }`
            ]
          ),
        },
      ].map((item, index) => (
        <HStack key={index} spacing="12px">
          <Icon as={item.icon} width="16px" height="16px" color="gray.500" />
          <Box>
            <Text fontSize={{ base: "0.875rem" }} m="0">
              {item?.filedName}
            </Text>
            <Text
              color="gray.900"
              fontWeight="bold"
              fontSize={{ base: "1rem" }}
              m="0"
            >
              {item?.filedValue}
            </Text>
          </Box>
        </HStack>
      ))}

      <Divider color="gray.300" />
      <Text
        fontSize={{ base: "1rem" }}
        m="0"
        _firstLetter={{ textTransform: "capitalize" }}
        whiteSpace="pre-line"
      >
        {serviceRequest?.description}
      </Text>

      <Grid
        gridTemplateColumns={{
          base: "repeat(auto-fill, 151px)",
          md: "repeat(auto-fill, 170px)",
          lg: "repeat(auto-fill, 190px)",
        }}
        width="full"
        gap="8px"
        justifyContent="center"
      >
        {pictures.map((item, index) => (
          <GridItem
            key={index}
            h={{ base: "151px", md: "170px", lg: "190px" }}
            w="inherit"
            borderRadius="10px"
            overflow="hidden"
          >
            <AspectRatio ratio={3 / 3}>
              <Image w="inherit" h="inherit" src={item?.normal} alt="Pics" />
            </AspectRatio>
          </GridItem>
        ))}
      </Grid>
    </VStack>
  );
};
